/**
 * 공통 날짜 포맷.
 *
 * 화면에서는 날짜 문자열을 직접 조립하지 않는다. 대신 쓰임새 이름(DatePattern)을 고르면
 * 이 모듈이 로케일별 패턴으로 풀어 준다. Microsoft 제품(.NET 표준 서식 d·D·M·Y·t·g, Office
 * 로케일 리소스)과 같은 방식으로, 코드에는 "무엇을 보여 줄지"만 남고 "어떻게 쓸지"는 로케일이 정한다.
 *
 * - 패턴 문자열은 date-fns `format` 토큰이다. 로케일을 추가하려면 PATTERNS에 한 줄을 더한다.
 * - 날짜 전용 값("YYYY-MM-DD")은 로컬 자정으로 읽는다. UTC로 읽으면 하루가 밀릴 수 있다.
 * - 상대 시각은 date-fns `formatDistance`가 제안하는 문구를 그대로 쓴다.
 */
import { format, formatDistance, type Locale } from "date-fns";
import { enUS, ko } from "date-fns/locale";
import { fromDay, type DayNumber, type ISODate } from "@/engine/date";

export type DateLocale = "ko" | "en";

/** 쓰임새 이름. 새 화면은 여기서 고르고, 목록에 없으면 이름을 추가한다(패턴을 인라인으로 쓰지 않는다). */
export type DatePattern =
  /** 2026년 09월 05일: 본문 문장, 속성 값, 날짜 입력 필드 (.NET D) */
  | "full"
  /** 2026년 09월 05일 (토): 요일이 판단에 필요한 속성·헤더 */
  | "fullWeekday"
  /** 09월 05일: 올해가 분명한 제목·문장 (.NET M) */
  | "monthDay"
  /** 09월 05일 (토): 날짜별 그룹 머리글 */
  | "monthDayWeekday"
  /** 09/05: 간트 바 라벨·마커·축, 좁은 패널의 목록 값 */
  | "short"
  /** 2026-09-05: 표 셀, KPI 값, 변경 이력, Excel (.NET d, ko-KR) */
  | "numeric"
  /** 2026년 09월: 달력·간트 상단 머리글, 월 단위 제목 (.NET Y) */
  | "yearMonth"
  /** 26.09: 월 단위 매트릭스 열 머리글 */
  | "yearMonthShort"
  /** 9월: 간트 월 눈금, 달력 1일 칸, 월 드롭다운 */
  | "monthName"
  /** 2026년 */
  | "year"
  /** 5: 달력·간트 일 눈금 */
  | "day"
  /** 토 */
  | "weekday"
  /** 14:30 (.NET t) */
  | "time"
  /** 14시30분. 날짜 묶음 안의 활동 시각 */
  | "clock"
  /** 09월 05일 14:30: 수정·첨부 시각 (.NET g) */
  | "dateTime"
  /** 2026-09-05 14:30: 연도가 필요한 시각, 내보내기 */
  | "numericDateTime"
  /** 2026-09-05 14:30:05: 로그 시각(초 단위로 순서를 읽어야 하는 곳) */
  | "logTime"
  /** 2026 Q3 */
  | "quarter"
  /** 26 Q3 */
  | "quarterShort";

const PATTERNS: Record<DateLocale, Record<DatePattern, string>> = {
  ko: {
    full: "yyyy년 MM월 dd일",
    fullWeekday: "yyyy년 MM월 dd일 (EEE)",
    monthDay: "MM월 dd일",
    monthDayWeekday: "MM월 dd일 (EEE)",
    short: "MM/dd",
    numeric: "yyyy-MM-dd",
    yearMonth: "yyyy년 MM월",
    yearMonthShort: "yy.MM",
    monthName: "LLL",
    year: "yyyy년",
    day: "d",
    weekday: "EEE",
    time: "HH:mm",
    clock: "H'시'mm'분'",
    dateTime: "MM월 dd일 HH:mm",
    numericDateTime: "yyyy-MM-dd HH:mm",
    logTime: "yyyy-MM-dd HH:mm:ss",
    quarter: "yyyy QQQ",
    quarterShort: "yy QQQ",
  },
  en: {
    full: "MMM d, yyyy",
    fullWeekday: "EEE, MMM d, yyyy",
    monthDay: "MMM d",
    monthDayWeekday: "EEE, MMM d",
    short: "M/d",
    numeric: "M/d/yyyy",
    yearMonth: "MMMM yyyy",
    yearMonthShort: "MMM yy",
    monthName: "LLL",
    year: "yyyy",
    day: "d",
    weekday: "EEE",
    time: "h:mm a",
    clock: "h:mm a",
    dateTime: "MMM d, h:mm a",
    numericDateTime: "M/d/yyyy h:mm a",
    logTime: "yyyy-MM-dd HH:mm:ss",
    quarter: "yyyy QQQ",
    quarterShort: "yy QQQ",
  },
};

/** 범위 구분자. CLDR 관례: 한국어는 물결, 영어는 앞뒤 공백 있는 en dash. */
const RANGE_SEPARATOR: Record<DateLocale, string> = { ko: " ~ ", en: " – " };

const DATE_FNS_LOCALE: Record<DateLocale, Locale> = { ko, en: enUS };

/** 값이 없을 때 표시. 표·목록에서 빈 칸 대신 쓴다. */
export const EMPTY_DATE = "—";

/** Excel 날짜 셀 서식. 화면의 "numeric"과 같은 모양이다. */
export const EXCEL_DATE_FORMAT = "yyyy-mm-dd";

let current: DateLocale = "ko";

export function getDateLocale(): DateLocale {
  return current;
}

/** 앱 로케일을 바꾼다. 다국어 전환이 생기면 이 한 곳만 호출한다. */
export function setDateLocale(locale: DateLocale): void {
  current = locale;
}

/** date-fns 로케일 객체. react-day-picker 등 라이브러리에 넘길 때 쓴다. */
export function dateFnsLocale(locale: DateLocale = current): Locale {
  return DATE_FNS_LOCALE[locale];
}

export function datePattern(pattern: DatePattern, locale: DateLocale = current): string {
  return PATTERNS[locale][pattern];
}

type DateInput = ISODate | Date | undefined | null;

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const YEAR_MONTH = /^\d{4}-\d{2}$/;

/** "YYYY-MM-DD"·"YYYY-MM"은 로컬 날짜로, 그 밖의 문자열은 ISO 시각으로 읽는다. */
function toDate(value: DateInput): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (DATE_ONLY.test(value)) {
    const [y, m, d] = value.split("-").map(Number) as [number, number, number];
    return new Date(y, m - 1, d);
  }
  if (YEAR_MONTH.test(value)) {
    const [y, m] = value.split("-").map(Number) as [number, number];
    return new Date(y, m - 1, 1);
  }
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/**
 * 날짜·시각 하나를 쓰임새 이름으로 포맷한다.
 * 값이 없거나 읽을 수 없으면 `empty`(기본 "—")를 돌려준다.
 */
export function formatDate(value: DateInput, pattern: DatePattern, opts: { empty?: string; locale?: DateLocale } = {}): string {
  const dt = toDate(value);
  if (!dt) return opts.empty ?? EMPTY_DATE;
  const locale = opts.locale ?? current;
  return format(dt, PATTERNS[locale][pattern], { locale: DATE_FNS_LOCALE[locale] });
}

/** 엔진의 DayNumber(UTC 기준 일 번호)를 포맷한다. 간트 눈금처럼 일 번호로 도는 곳에서 쓴다. */
export function formatDayNumber(day: DayNumber, pattern: DatePattern, opts: { locale?: DateLocale } = {}): string {
  return formatDate(fromDay(day), pattern, opts);
}

/**
 * 기간. 시작과 끝이 같으면 한 번만 쓴다. 한쪽이 비면 그 자리는 "—".
 * 예) numeric → "2026-09-05 ~ 2026-09-18", short → "09/05 ~ 09/18"
 */
export function formatDateRange(start: DateInput, end: DateInput, pattern: DatePattern, opts: { locale?: DateLocale } = {}): string {
  if (!start && !end) return EMPTY_DATE;
  const a = formatDate(start, pattern, opts);
  const b = formatDate(end, pattern, opts);
  if (a === b) return a;
  return `${a}${RANGE_SEPARATOR[opts.locale ?? current]}${b}`;
}

/**
 * 같은 해·같은 달의 기간은 끝의 월을 빼서 짧게 쓴다.
 * ko: "02월 16일 ~ 18일", 달이 바뀌면 "01월 31일 ~ 02월 02일"
 * en: "Feb 16 – 18", "Jan 31 – Feb 2"
 * 해가 바뀌면 full 기간이다.
 */
export function formatMonthDaySpan(start: DateInput, end: DateInput, opts: { locale?: DateLocale } = {}): string {
  const a = toDate(start);
  const b = toDate(end);
  if (!a && !b) return EMPTY_DATE;
  if (!a || !b) return formatDate(a ? start : end, "monthDay", opts);
  const locale = opts.locale ?? current;
  const [from, to] = a.getTime() <= b.getTime() ? [a, b] : [b, a];
  if (from.getTime() === to.getTime()) return formatDate(from, "monthDay", { locale });
  if (from.getFullYear() !== to.getFullYear()) return formatDateRange(from, to, "full", { locale });
  if (from.getMonth() !== to.getMonth()) return formatDateRange(from, to, "monthDay", { locale });
  const loc = DATE_FNS_LOCALE[locale];
  const head = format(from, locale === "ko" ? "MM'월' dd'일'" : "MMM d", { locale: loc });
  const tail = format(to, locale === "ko" ? "dd'일'" : "d", { locale: loc });
  return `${head}${RANGE_SEPARATOR[locale]}${tail}`;
}

/** 범위 구분자만 필요할 때(값 사이에 다른 표기가 끼는 경우). */
export function dateRangeSeparator(locale: DateLocale = current): string {
  return RANGE_SEPARATOR[locale];
}

/**
 * 상대 시각. date-fns 문구를 쓰되, 한국어 앞의 「약」은 뺀다.
 * 예) "1분 미만 전", "5분 전", "3시간 전", "3일 전", "3일 후"
 * 읽을 수 없는 값은 원문을 돌려준다.
 */
export function formatAgo(value: string | Date, opts: { now?: number | Date; locale?: DateLocale } = {}): string {
  const dt = toDate(value);
  if (!dt) return typeof value === "string" ? value : EMPTY_DATE;
  const locale = opts.locale ?? current;
  const text = formatDistance(dt, opts.now ?? Date.now(), { addSuffix: true, locale: DATE_FNS_LOCALE[locale] });
  return locale === "ko" ? text.replace(/^약 /, "") : text;
}

/** 가까운 날은 "오늘"·"어제", 그 밖에는 요일을 붙인 날짜. 올해가 아니면 연도까지. */
export function formatDayHeading(value: DateInput, opts: { now?: Date; locale?: DateLocale } = {}): string {
  const dt = toDate(value);
  if (!dt) return EMPTY_DATE;
  const locale = opts.locale ?? current;
  const now = opts.now ?? new Date();
  const same = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (same(dt, now)) return RELATIVE_DAY[locale].today;
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (same(dt, yesterday)) return RELATIVE_DAY[locale].yesterday;
  return formatDate(dt, dt.getFullYear() === now.getFullYear() ? "monthDayWeekday" : "fullWeekday", { locale });
}

const RELATIVE_DAY: Record<DateLocale, { today: string; yesterday: string }> = {
  ko: { today: "오늘", yesterday: "어제" },
  en: { today: "Today", yesterday: "Yesterday" },
};

export type DueTone = "overdue" | "today" | "soon" | "later";

const RELATIVE_DUE: Record<DateLocale, { overdue: (days: number) => string; today: string; tomorrow: string }> = {
  ko: { overdue: (n) => `${n}일 지남`, today: "오늘", tomorrow: "내일" },
  en: { overdue: (n) => `${n}d overdue`, today: "Today", tomorrow: "Tomorrow" },
};

/**
 * 마감일을 오늘 기준의 말로 쓴다(목록 행처럼 빨리 읽어야 하는 곳).
 * 예) "3일 지남" · "오늘" · "내일" · "금요일"(6일 이내) · "10월 30일" · 올해가 아니면 연도까지.
 * `tone`으로 색을 고른다(overdue = 위험, today = 주의).
 */
export function formatDueRelative(due: ISODate, today: ISODate, opts: { locale?: DateLocale } = {}): { text: string; tone: DueTone } {
  const d = toDate(due);
  const t = toDate(today);
  if (!d || !t) return { text: EMPTY_DATE, tone: "later" };
  const locale = opts.locale ?? current;
  const w = RELATIVE_DUE[locale];
  // local midnights; round absorbs DST 23h/25h days
  const diff = Math.round((d.getTime() - t.getTime()) / 86_400_000);
  if (diff < 0) return { text: w.overdue(-diff), tone: "overdue" };
  if (diff === 0) return { text: w.today, tone: "today" };
  if (diff === 1) return { text: w.tomorrow, tone: "soon" };
  if (diff < 7) return { text: format(d, "EEEE", { locale: DATE_FNS_LOCALE[locale] }), tone: "soon" };
  return { text: formatDate(d, d.getFullYear() === t.getFullYear() ? "monthDay" : "full", { locale }), tone: "later" };
}

/** 요일 이름. 0 = 일요일. short는 "일"/"Sun", long은 "일요일"/"Sunday". */
export function weekdayNames(width: "short" | "long" = "short", locale: DateLocale = current): string[] {
  const token = width === "short" ? "EEE" : "EEEE";
  // 2026-01-04 is a Sunday.
  return Array.from({ length: 7 }, (_, i) => format(new Date(2026, 0, 4 + i), token, { locale: DATE_FNS_LOCALE[locale] }));
}

/** 월 이름. 0 = 1월. */
export function monthNames(width: "short" | "long" = "short", locale: DateLocale = current): string[] {
  const token = width === "short" ? "LLL" : "LLLL";
  return Array.from({ length: 12 }, (_, i) => format(new Date(2026, i, 1), token, { locale: DATE_FNS_LOCALE[locale] }));
}

const DAYS_UNTIL: Record<DateLocale, (days: number) => string> = {
  ko: (n) => (n > 0 ? `D-${n}` : n === 0 ? "D-day" : `D+${-n}`),
  en: (n) => (n > 0 ? `D-${n}` : n === 0 ? "D-day" : `D+${-n}`),
};

/** 오늘부터 그 날까지 남은 일수. 지난 날은 음수. 읽을 수 없으면 null. */
export function daysUntil(value: DateInput, today: DateInput): number | null {
  const d = toDate(value);
  const t = toDate(today);
  if (!d || !t) return null;
  return Math.round((d.getTime() - t.getTime()) / 86_400_000);
}

/**
 * 만료까지 남은 날. D-3, 당일은 D-day, 지난 날은 D+2.
 * 화면에서 `D-${n}`을 조립하지 않는다.
 */
export function formatDaysUntil(value: DateInput, today: DateInput, opts: { locale?: DateLocale } = {}): string {
  const diff = daysUntil(value, today);
  if (diff == null) return EMPTY_DATE;
  return DAYS_UNTIL[opts.locale ?? current](diff);
}

/** 디자인 시스템 표에 쓰는 전체 목록. 순서는 문서 순서. */
export const DATE_PATTERN_SPECS: ReadonlyArray<{ pattern: DatePattern; use: string }> = [
  { pattern: "full", use: "본문 문장, 속성 값, 날짜 입력 필드" },
  { pattern: "fullWeekday", use: "요일이 필요한 속성(시작·종료), 캘린더 팝오버 머리글" },
  { pattern: "monthDay", use: "올해가 분명한 제목·문장" },
  { pattern: "monthDayWeekday", use: "날짜별 그룹 머리글, 공휴일 목록" },
  { pattern: "short", use: "간트 바 라벨·마커·눈금, 차트 축, 좁은 패널의 목록 값" },
  { pattern: "numeric", use: "표 셀, KPI 값, 변경 이력, Excel" },
  { pattern: "yearMonth", use: "달력·간트 상단 머리글, 월 단위 제목" },
  { pattern: "yearMonthShort", use: "월 단위 매트릭스 열 머리글" },
  { pattern: "monthName", use: "간트 월 눈금, 달력 1일 칸, 월 드롭다운" },
  { pattern: "year", use: "간트 연 머리글, 연도 필터" },
  { pattern: "day", use: "달력·간트 일 눈금" },
  { pattern: "weekday", use: "요일 머리글" },
  { pattern: "time", use: "같은 날 안의 시각" },
  { pattern: "clock", use: "활동 목록의 시각" },
  { pattern: "dateTime", use: "수정·첨부·스냅샷 시각" },
  { pattern: "numericDateTime", use: "연도가 필요한 시각, 내보내기" },
  { pattern: "logTime", use: "로그 시각(초 단위)" },
  { pattern: "quarter", use: "분기 행 라벨" },
  { pattern: "quarterShort", use: "분기 열 머리글" },
];
